import React, { useEffect, useState } from 'react';
import { UmbrellaIcon, SunIcon, ShoppingBagIcon, LinkIcon, EyeIcon, XIcon, ShirtIcon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
export function OutfitRecommendation({
  weatherData,
  userProfile = {}
}) {
  const [selectedOutfit, setSelectedOutfit] = useState(null);
  const [showOutfitModal, setShowOutfitModal] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [displayedOutfits, setDisplayedOutfits] = useState([]);
  // Function to generate outfit recommendations based on weather, region, and user profile
  const getRecommendations = () => {
    const {
      temperature,
      condition,
      season
    } = weatherData;
    const {
      gender,
      style,
      bodyType
    } = userProfile;
    let recommendations = {
      top: [],
      bottom: [],
      accessories: [],
      footwear: []
    };
    // Base recommendations by temperature - more granular temperature ranges
    if (temperature >= 85) {
      // Very hot
      recommendations = {
        top: ['Lightweight T-shirt', 'Breathable tank top', 'Linen shirt'],
        bottom: ['Shorts', 'Light skirt', 'Linen pants'],
        accessories: ['Sunglasses', 'Wide-brim hat', 'Sunscreen', 'Water bottle'],
        footwear: ['Sandals', 'Flip flops', 'Breathable sneakers']
      };
    } else if (temperature >= 70 && temperature < 85) {
      // Warm
      recommendations = {
        top: ['Light T-shirt', 'Short-sleeve shirt', 'Polo shirt'],
        bottom: ['Shorts', 'Skirt', 'Light chinos'],
        accessories: ['Sunglasses', 'Cap', 'Sunscreen'],
        footwear: ['Light sneakers', 'Loafers', 'Canvas shoes']
      };
    } else if (temperature >= 55 && temperature < 70) {
      // Moderate
      recommendations = {
        top: ['Long-sleeve shirt', 'Light sweater', 'Button-up shirt'],
        bottom: ['Jeans', 'Chinos', 'Casual pants'],
        accessories: condition.toLowerCase().includes('rain') ? ['Umbrella', 'Light jacket'] : ['Light scarf', 'Sunglasses'],
        footwear: ['Sneakers', 'Loafers', 'Casual shoes']
      };
    } else if (temperature >= 40 && temperature < 55) {
      // Cool
      recommendations = {
        top: ['Sweater', 'Sweatshirt', 'Thermal shirt', 'Light jacket'],
        bottom: ['Warm pants', 'Jeans', 'Wool trousers'],
        accessories: ['Scarf', 'Light gloves', 'Beanie'],
        footwear: ['Boots', 'Closed shoes', 'Sneakers']
      };
    } else {
      // Cold (below 40)
      recommendations = {
        top: ['Heavy sweater', 'Thermal shirt', 'Fleece', 'Winter coat'],
        bottom: ['Insulated pants', 'Heavy jeans', 'Wool trousers'],
        accessories: ['Thick scarf', 'Gloves', 'Beanie', 'Ear muffs'],
        footwear: ['Winter boots', 'Insulated shoes', 'Wool socks']
      };
    }
    // Regional adjustments based on selected city
    const cityName = weatherData.cityName || '';
    // North American adjustments
    if (['New York', 'Chicago', 'Toronto', 'Montreal', 'Ottawa', 'Calgary'].includes(cityName)) {
      // Add layering options for variable weather in these cities
      if (temperature < 55) {
        recommendations.top.push('Layering pieces', 'Thermal underlayer');
        recommendations.accessories.push('Wind-resistant jacket');
      }
      // Canadian cities specific items
      if (['Toronto', 'Montreal', 'Ottawa', 'Calgary'].includes(cityName)) {
        if (temperature < 40) {
          recommendations.accessories.push('Thermal face mask', 'Hand warmers');
          recommendations.top.push('Canadian-made parka');
        }
      }
    }
    // West Coast adjustments
    if (['Vancouver', 'Seattle', 'Portland', 'San Francisco'].includes(cityName)) {
      if (condition.toLowerCase().includes('rain')) {
        recommendations.accessories.push('Waterproof jacket', 'Rain boots');
        recommendations.top.push('Water-resistant layers');
      }
    }
    // Southern cities adjustments
    if (['Miami', 'Los Angeles', 'Phoenix', 'Houston'].includes(cityName)) {
      if (temperature > 80) {
        recommendations.top.push('UV-protective clothing');
        recommendations.accessories.push('Cooling towel', 'Portable fan');
      }
    }
    // European adjustments
    if (['London', 'Paris', 'Berlin', 'Rome', 'Barcelona'].includes(cityName)) {
      // European fashion tends to be more formal
      recommendations.top = recommendations.top.map(item => item.includes('T-shirt') ? item.replace('T-shirt', 'fitted tee') : item);
      recommendations.bottom = recommendations.bottom.map(item => item.includes('pants') ? item.replace('pants', 'trousers') : item);
      // London and Paris specific
      if (['London', 'Paris'].includes(cityName)) {
        recommendations.accessories.push('Stylish umbrella');
        recommendations.footwear.push('Chelsea boots');
      }
    }
    // Asian cities adjustments
    if (['Tokyo', 'Seoul', 'Singapore', 'Hong Kong'].includes(cityName)) {
      recommendations.accessories.push('Fashionable face mask');
      if (['Tokyo', 'Seoul'].includes(cityName)) {
        recommendations.top.push('Layered outfit');
        recommendations.accessories.push('Crossbody bag');
      }
      if (['Singapore', 'Hong Kong'].includes(cityName)) {
        recommendations.top.push('Breathable fabrics');
        recommendations.accessories.push('Portable umbrella');
      }
    }
    // Oceania adjustments
    if (['Sydney', 'Melbourne', 'Auckland'].includes(cityName)) {
      recommendations.accessories.push('UV-protective sunglasses');
      recommendations.top.push('UPF-rated clothing');
      if (condition.toLowerCase().includes('rain')) {
        recommendations.accessories.push('Quick-dry jacket');
      }
    }
    // Modify recommendations based on style preference
    if (style) {
      switch (style.toLowerCase()) {
        case 'gothic':
          recommendations.top = recommendations.top.map(item => `Black ${item}`);
          recommendations.accessories.push('Dark makeup', 'Silver jewelry');
          break;
        case 'classic':
          recommendations.top.push('Timeless blazer', 'Button-down shirt');
          recommendations.bottom = recommendations.bottom.map(item => `Classic ${item}`);
          break;
        case 'bohemian':
          recommendations.accessories.push('Layered jewelry', 'Headband');
          recommendations.top.push('Flowy blouse');
          break;
        case 'formal':
          recommendations.top = ['Dress shirt', 'Blazer'];
          recommendations.bottom = ['Dress pants', 'Pencil skirt'];
          recommendations.footwear = ['Dress shoes', 'Heels'];
          break;
        case 'sporty':
          recommendations.top.push('Athletic shirt', 'Performance top');
          recommendations.bottom.push('Athletic shorts', 'Leggings');
          recommendations.footwear = ['Athletic shoes', 'Sneakers'];
          break;
        // More style cases can be added
      }
    }
    // Modify recommendations based on body type
    if (bodyType) {
      switch (bodyType.toLowerCase()) {
        case 'hourglass':
          recommendations.top.push('Fitted top');
          recommendations.bottom.push('High-waisted bottom');
          break;
        case 'pear':
          recommendations.top.push('Boat neck top', 'Structured shoulders');
          recommendations.bottom.push('A-line skirt');
          break;
        case 'apple':
          recommendations.top.push('V-neck top', 'Empire waist');
          recommendations.bottom.push('Straight leg pants');
          break;
        case 'athletic':
          recommendations.top.push('Ruffled top');
          recommendations.bottom.push('Cargo pants', 'Pleated skirt');
          break;
        // More body type cases can be added
      }
    }
    // Make recommendations unique
    Object.keys(recommendations).forEach(key => {
      recommendations[key] = [...new Set(recommendations[key])];
    });
    return recommendations;
  };
  // Function to recommend stores based on region and style
  const getStoreRecommendations = () => {
    const cityName = weatherData.cityName || '';
    const {
      style = ''
    } = userProfile;
    // Base stores available in most regions
    let stores = ['H&M', 'Zara', 'Uniqlo', 'ASOS'];
    // Region-specific stores
    if (['New York', 'Chicago', 'Los Angeles', 'Miami'].includes(cityName)) {
      stores.push("Macy's", 'Nordstrom', 'Target', 'Old Navy');
    }
    if (['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa'].includes(cityName)) {
      stores.push("Hudson's Bay", 'Simons', 'Roots', 'Sport Chek');
      // Add specific Canadian outdoor stores for cold weather cities
      if (['Toronto', 'Montreal', 'Ottawa', 'Calgary'].includes(cityName) && weatherData.temperature < 40) {
        stores.push('Canada Goose', 'MEC (Mountain Equipment Co-op)');
      }
    }
    if (['London', 'Paris', 'Berlin', 'Rome', 'Barcelona'].includes(cityName)) {
      stores.push('Marks & Spencer', 'Primark', 'Mango');
      if (cityName === 'Paris') {
        stores.push('Galeries Lafayette', 'Printemps');
      } else if (cityName === 'London') {
        stores.push('Selfridges', 'Harrods');
      }
    }
    if (['Tokyo', 'Seoul', 'Singapore', 'Hong Kong', 'Dubai'].includes(cityName)) {
      stores.push('Muji', 'GU');
      if (cityName === 'Tokyo') {
        stores.push('Don Quijote', 'Shibuya 109');
      } else if (cityName === 'Seoul') {
        stores.push('Stylenanda', 'Åland');
      }
    }
    if (['Sydney', 'Melbourne', 'Auckland'].includes(cityName)) {
      stores.push('Cotton On', 'Just Jeans', 'Myer', 'David Jones');
    }
    // Style-specific stores
    if (style) {
      switch (style.toLowerCase()) {
        case 'gothic':
          stores.push('Hot Topic', 'Killstar');
          break;
        case 'classic':
          stores.push('Brooks Brothers', 'Banana Republic', 'J.Crew');
          break;
        case 'bohemian':
          stores.push('Free People', 'Anthropologie', 'Urban Outfitters');
          break;
        case 'formal':
          stores.push('Hugo Boss', 'Ann Taylor', 'Express');
          break;
        case 'sporty':
          stores.push('Nike', 'Adidas', 'Lululemon', 'Under Armour');
          break;
        case 'minimalist':
          stores.push('COS', 'Everlane', 'Arket');
          break;
        case 'vintage':
          stores.push('Thrift stores', 'Vintage boutiques', 'Depop', 'Etsy');
          break;
        case 'streetwear':
          stores.push('Supreme', 'Stüssy', 'Urban Outfitters');
          break;
      }
    }
    // Online options for all regions
    stores.push('Amazon Fashion', 'ASOS', 'Net-a-Porter');
    // Make store list unique and limit to 6 stores
    return [...new Set(stores)].slice(0, 6);
  };
  // Function to generate complete outfit examples
  const getOutfitExamples = () => {
    const {
      temperature,
      condition,
      cityName = ''
    } = weatherData;
    const {
      style = '',
      gender = '',
      bodyType = ''
    } = userProfile;
    // Base outfit examples
    let outfits = [];
    // Default store domains for generating mock links
    const storeDomains = {
      'H&M': 'hm.com',
      Zara: 'zara.com',
      Uniqlo: 'uniqlo.com',
      ASOS: 'asos.com',
      "Macy's": 'macys.com',
      Nordstrom: 'nordstrom.com',
      Target: 'target.com',
      'Old Navy': 'oldnavy.com',
      "Hudson's Bay": 'thebay.com',
      Simons: 'simons.ca',
      Roots: 'roots.com',
      'Sport Chek': 'sportchek.ca',
      'Marks & Spencer': 'marksandspencer.com',
      Primark: 'primark.com',
      Mango: 'mango.com',
      Muji: 'muji.com',
      GU: 'gu-global.com',
      'Cotton On': 'cottonon.com',
      'Just Jeans': 'justjeans.com.au',
      Myer: 'myer.com.au',
      Nike: 'nike.com',
      Adidas: 'adidas.com',
      Lululemon: 'lululemon.com',
      'Free People': 'freepeople.com',
      'Urban Outfitters': 'urbanoutfitters.com',
      'Hot Topic': 'hottopic.com',
      'Amazon Fashion': 'amazon.com/fashion',
      'Net-a-Porter': 'net-a-porter.com',
      'Canada Goose': 'canadagoose.com',
      MEC: 'mec.ca'
    };
    // Helper function to create a mock product link
    const createProductLink = (item, store) => {
      const domain = storeDomains[store] || 'example.com';
      const itemSlug = item.toLowerCase().replace(/\s+/g, '-');
      return `https://www.${domain}/products/${itemSlug}`;
    };
    // Very Hot Weather (85°F and above)
    if (temperature >= 85) {
      outfits.push({
        name: 'Summer Casual',
        image: 'https://images.unsplash.com/photo-1613552465135-e5bba88c5bca?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Breathable Linen Shirt',
          store: 'Uniqlo',
          link: createProductLink('Breathable Linen Shirt', 'Uniqlo')
        }, {
          category: 'bottom',
          name: 'Light Cotton Shorts',
          store: 'H&M',
          link: createProductLink('Light Cotton Shorts', 'H&M')
        }, {
          category: 'accessories',
          name: 'Wide-Brim Sun Hat',
          store: 'ASOS',
          link: createProductLink('Wide-Brim Sun Hat', 'ASOS')
        }, {
          category: 'accessories',
          name: 'Polarized Sunglasses',
          store: 'Nordstrom',
          link: createProductLink('Polarized Sunglasses', 'Nordstrom')
        }, {
          category: 'accessories',
          name: 'SPF 50 Sunscreen',
          store: 'Target',
          link: createProductLink('SPF 50 Sunscreen', 'Target')
        }, {
          category: 'footwear',
          name: 'Leather Sandals',
          store: 'Zara',
          link: createProductLink('Leather Sandals', 'Zara')
        }]
      });
      outfits.push({
        name: 'Beach Day',
        image: 'https://images.unsplash.com/photo-1537631435086-2809aeca8be5?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Loose Tank Top',
          store: 'H&M',
          link: createProductLink('Loose Tank Top', 'H&M')
        }, {
          category: 'bottom',
          name: 'Lightweight Linen Pants',
          store: 'Zara',
          link: createProductLink('Lightweight Linen Pants', 'Zara')
        }, {
          category: 'accessories',
          name: 'Straw Hat',
          store: 'Mango',
          link: createProductLink('Straw Hat', 'Mango')
        }, {
          category: 'accessories',
          name: 'Beach Tote Bag',
          store: 'Old Navy',
          link: createProductLink('Beach Tote Bag', 'Old Navy')
        }, {
          category: 'accessories',
          name: 'Water-Resistant Smartwatch',
          store: 'Amazon Fashion',
          link: createProductLink('Water-Resistant Smartwatch', 'Amazon Fashion')
        }, {
          category: 'footwear',
          name: 'Flip Flops',
          store: 'Old Navy',
          link: createProductLink('Flip Flops', 'Old Navy')
        }]
      });
      // Adding more hot weather options
      outfits.push({
        name: 'Tropical Vacation',
        image: 'https://images.unsplash.com/photo-1517401360673-15225f0c5b6f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Tropical Print Shirt',
          store: 'Zara',
          link: createProductLink('Tropical Print Shirt', 'Zara')
        }, {
          category: 'bottom',
          name: 'Quick-Dry Swim Shorts',
          store: 'H&M',
          link: createProductLink('Quick-Dry Swim Shorts', 'H&M')
        }, {
          category: 'accessories',
          name: 'Woven Straw Hat',
          store: 'ASOS',
          link: createProductLink('Woven Straw Hat', 'ASOS')
        }, {
          category: 'footwear',
          name: 'Waterproof Sandals',
          store: 'Adidas',
          link: createProductLink('Waterproof Sandals', 'Adidas')
        }]
      });
      outfits.push({
        name: 'Urban Summer',
        image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Sleeveless Blouse',
          store: 'Mango',
          link: createProductLink('Sleeveless Blouse', 'Mango')
        }, {
          category: 'bottom',
          name: 'Linen Culottes',
          store: 'Uniqlo',
          link: createProductLink('Linen Culottes', 'Uniqlo')
        }, {
          category: 'accessories',
          name: 'Minimalist Sunglasses',
          store: 'Zara',
          link: createProductLink('Minimalist Sunglasses', 'Zara')
        }, {
          category: 'footwear',
          name: 'Espadrille Wedges',
          store: 'ASOS',
          link: createProductLink('Espadrille Wedges', 'ASOS')
        }]
      });
    }
    // Warm Weather (70-84°F)
    if (temperature >= 70 && temperature < 85) {
      outfits.push({
        name: 'Spring Day Out',
        image: 'https://images.unsplash.com/photo-1622519407650-3df9883f76a5?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Cotton Polo Shirt',
          store: 'Uniqlo',
          link: createProductLink('Cotton Polo Shirt', 'Uniqlo')
        }, {
          category: 'bottom',
          name: 'Chino Shorts',
          store: 'H&M',
          link: createProductLink('Chino Shorts', 'H&M')
        }, {
          category: 'accessories',
          name: 'Canvas Belt',
          store: 'Zara',
          link: createProductLink('Canvas Belt', 'Zara')
        }, {
          category: 'accessories',
          name: 'Aviator Sunglasses',
          store: 'Nordstrom',
          link: createProductLink('Aviator Sunglasses', 'Nordstrom')
        }, {
          category: 'footwear',
          name: 'Canvas Sneakers',
          store: 'ASOS',
          link: createProductLink('Canvas Sneakers', 'ASOS')
        }]
      });
      outfits.push({
        name: 'Outdoor Brunch',
        image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Short-Sleeve Button-Up',
          store: 'J.Crew',
          link: createProductLink('Short-Sleeve Button-Up', 'J.Crew')
        }, {
          category: 'bottom',
          name: 'Light Wash Jeans',
          store: "Levi's",
          link: createProductLink('Light Wash Jeans', "Levi's")
        }, {
          category: 'accessories',
          name: 'Minimalist Watch',
          store: 'Nordstrom',
          link: createProductLink('Minimalist Watch', 'Nordstrom')
        }, {
          category: 'accessories',
          name: 'Straw Fedora',
          store: 'Mango',
          link: createProductLink('Straw Fedora', 'Mango')
        }, {
          category: 'footwear',
          name: 'Suede Loafers',
          store: 'ASOS',
          link: createProductLink('Suede Loafers', 'ASOS')
        }]
      });
      // Adding more warm weather options
      outfits.push({
        name: 'Casual Friday',
        image: 'https://images.unsplash.com/photo-1530268729831-4b0b9e170218?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Light Knit Sweater',
          store: 'Uniqlo',
          link: createProductLink('Light Knit Sweater', 'Uniqlo')
        }, {
          category: 'bottom',
          name: 'Slim Chinos',
          store: 'Banana Republic',
          link: createProductLink('Slim Chinos', 'Banana Republic')
        }, {
          category: 'accessories',
          name: 'Leather Watch',
          store: 'Fossil',
          link: createProductLink('Leather Watch', 'Fossil')
        }, {
          category: 'footwear',
          name: 'Leather Sneakers',
          store: 'Adidas',
          link: createProductLink('Leather Sneakers', 'Adidas')
        }]
      });
      outfits.push({
        name: 'Weekend Getaway',
        image: 'https://images.unsplash.com/photo-1492707892479-7bc8d5a4ee93?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Breezy Blouse',
          store: 'Mango',
          link: createProductLink('Breezy Blouse', 'Mango')
        }, {
          category: 'bottom',
          name: 'Midi Skirt',
          store: 'H&M',
          link: createProductLink('Midi Skirt', 'H&M')
        }, {
          category: 'accessories',
          name: 'Straw Crossbody Bag',
          store: 'Zara',
          link: createProductLink('Straw Crossbody Bag', 'Zara')
        }, {
          category: 'footwear',
          name: 'Strappy Sandals',
          store: 'ASOS',
          link: createProductLink('Strappy Sandals', 'ASOS')
        }]
      });
    }
    // Moderate Weather (55-69°F)
    if (temperature >= 55 && temperature < 70) {
      outfits.push({
        name: 'Casual Office Day',
        image: 'https://images.unsplash.com/photo-1580657018950-c7f7d6a6d990?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Merino Wool Sweater',
          store: 'Uniqlo',
          link: createProductLink('Merino Wool Sweater', 'Uniqlo')
        }, {
          category: 'bottom',
          name: 'Slim Fit Chinos',
          store: 'Banana Republic',
          link: createProductLink('Slim Fit Chinos', 'Banana Republic')
        }, {
          category: 'accessories',
          name: 'Leather Belt',
          store: 'Nordstrom',
          link: createProductLink('Leather Belt', 'Nordstrom')
        }, {
          category: 'accessories',
          name: 'Leather Messenger Bag',
          store: "Macy's",
          link: createProductLink('Leather Messenger Bag', "Macy's")
        }, {
          category: 'footwear',
          name: 'Leather Oxfords',
          store: 'ASOS',
          link: createProductLink('Leather Oxfords', 'ASOS')
        }]
      });
      outfits.push({
        name: 'Weekend Coffee Run',
        image: 'https://images.unsplash.com/photo-1495385794356-15371f348c31?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Henley Long-Sleeve',
          store: 'H&M',
          link: createProductLink('Henley Long-Sleeve', 'H&M')
        }, {
          category: 'top',
          name: 'Light Denim Jacket',
          store: "Levi's",
          link: createProductLink('Light Denim Jacket', "Levi's")
        }, {
          category: 'bottom',
          name: 'Dark Wash Jeans',
          store: 'Uniqlo',
          link: createProductLink('Dark Wash Jeans', 'Uniqlo')
        }, {
          category: 'accessories',
          name: 'Knit Beanie',
          store: 'Urban Outfitters',
          link: createProductLink('Knit Beanie', 'Urban Outfitters')
        }, {
          category: 'footwear',
          name: 'Leather Boots',
          store: 'ASOS',
          link: createProductLink('Leather Boots', 'ASOS')
        }]
      });
      // Adding more moderate weather options
      outfits.push({
        name: 'Autumn Stroll',
        image: 'https://images.unsplash.com/photo-1511090289856-e36004532563?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Turtleneck Sweater',
          store: 'Uniqlo',
          link: createProductLink('Turtleneck Sweater', 'Uniqlo')
        }, {
          category: 'bottom',
          name: 'Straight Leg Jeans',
          store: "Levi's",
          link: createProductLink('Straight Leg Jeans', "Levi's")
        }, {
          category: 'accessories',
          name: 'Light Scarf',
          store: 'Zara',
          link: createProductLink('Light Scarf', 'Zara')
        }, {
          category: 'footwear',
          name: 'Ankle Boots',
          store: 'ASOS',
          link: createProductLink('Ankle Boots', 'ASOS')
        }]
      });
      outfits.push({
        name: 'Business Casual',
        image: 'https://images.unsplash.com/photo-1541779408-c1f32d379fef?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Cashmere Blend Sweater',
          store: 'J.Crew',
          link: createProductLink('Cashmere Blend Sweater', 'J.Crew')
        }, {
          category: 'bottom',
          name: 'Wool Blend Trousers',
          store: 'Banana Republic',
          link: createProductLink('Wool Blend Trousers', 'Banana Republic')
        }, {
          category: 'accessories',
          name: 'Leather Portfolio',
          store: 'Nordstrom',
          link: createProductLink('Leather Portfolio', 'Nordstrom')
        }, {
          category: 'footwear',
          name: 'Leather Derby Shoes',
          store: 'ASOS',
          link: createProductLink('Leather Derby Shoes', 'ASOS')
        }]
      });
    }
    // Cool Weather (40-54°F)
    if (temperature >= 40 && temperature < 55) {
      outfits.push({
        name: 'Fall Layers',
        image: 'https://images.unsplash.com/photo-1559582798-678dfc71ccd8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Thermal Henley',
          store: 'Uniqlo',
          link: createProductLink('Thermal Henley', 'Uniqlo')
        }, {
          category: 'top',
          name: 'Flannel Shirt',
          store: 'J.Crew',
          link: createProductLink('Flannel Shirt', 'J.Crew')
        }, {
          category: 'top',
          name: 'Quilted Vest',
          store: 'Nordstrom',
          link: createProductLink('Quilted Vest', 'Nordstrom')
        }, {
          category: 'bottom',
          name: 'Corduroy Pants',
          store: 'Banana Republic',
          link: createProductLink('Corduroy Pants', 'Banana Republic')
        }, {
          category: 'accessories',
          name: 'Wool Scarf',
          store: 'Zara',
          link: createProductLink('Wool Scarf', 'Zara')
        }, {
          category: 'footwear',
          name: 'Leather Chukka Boots',
          store: 'ASOS',
          link: createProductLink('Leather Chukka Boots', 'ASOS')
        }]
      });
      outfits.push({
        name: 'Rainy Day',
        image: 'https://images.unsplash.com/photo-1523471826770-c437b4636fe6?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Turtleneck Sweater',
          store: 'Uniqlo',
          link: createProductLink('Turtleneck Sweater', 'Uniqlo')
        }, {
          category: 'top',
          name: 'Waterproof Rain Jacket',
          store: 'Nordstrom',
          link: createProductLink('Waterproof Rain Jacket', 'Nordstrom')
        }, {
          category: 'bottom',
          name: 'Slim Fit Jeans',
          store: "Levi's",
          link: createProductLink('Slim Fit Jeans', "Levi's")
        }, {
          category: 'accessories',
          name: 'Compact Umbrella',
          store: 'Amazon Fashion',
          link: createProductLink('Compact Umbrella', 'Amazon Fashion')
        }, {
          category: 'accessories',
          name: 'Waterproof Phone Case',
          store: 'Amazon Fashion',
          link: createProductLink('Waterproof Phone Case', 'Amazon Fashion')
        }, {
          category: 'footwear',
          name: 'Waterproof Chelsea Boots',
          store: 'ASOS',
          link: createProductLink('Waterproof Chelsea Boots', 'ASOS')
        }]
      });
      // Adding more cool weather options
      outfits.push({
        name: 'Autumn Weekend',
        image: 'https://images.unsplash.com/photo-1504703395950-b89145a5425b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Chunky Knit Cardigan',
          store: 'Zara',
          link: createProductLink('Chunky Knit Cardigan', 'Zara')
        }, {
          category: 'top',
          name: 'Soft Cotton T-shirt',
          store: 'Uniqlo',
          link: createProductLink('Soft Cotton T-shirt', 'Uniqlo')
        }, {
          category: 'bottom',
          name: 'Relaxed Fit Jeans',
          store: "Levi's",
          link: createProductLink('Relaxed Fit Jeans', "Levi's")
        }, {
          category: 'accessories',
          name: 'Leather Crossbody Bag',
          store: 'Mango',
          link: createProductLink('Leather Crossbody Bag', 'Mango')
        }, {
          category: 'footwear',
          name: 'Suede Chelsea Boots',
          store: 'ASOS',
          link: createProductLink('Suede Chelsea Boots', 'ASOS')
        }]
      });
      outfits.push({
        name: 'Outdoor Adventure',
        image: 'https://images.unsplash.com/photo-1527254042638-ebb45677d6c3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Performance Base Layer',
          store: 'The North Face',
          link: createProductLink('Performance Base Layer', 'The North Face')
        }, {
          category: 'top',
          name: 'Fleece Quarter-Zip',
          store: 'Columbia',
          link: createProductLink('Fleece Quarter-Zip', 'Columbia')
        }, {
          category: 'bottom',
          name: 'Water-Resistant Hiking Pants',
          store: 'Columbia',
          link: createProductLink('Water-Resistant Hiking Pants', 'Columbia')
        }, {
          category: 'accessories',
          name: 'Knit Beanie',
          store: 'The North Face',
          link: createProductLink('Knit Beanie', 'The North Face')
        }, {
          category: 'footwear',
          name: 'Waterproof Hiking Boots',
          store: 'Columbia',
          link: createProductLink('Waterproof Hiking Boots', 'Columbia')
        }]
      });
    }
    // Cold Weather (Below 40°F)
    if (temperature < 40) {
      outfits.push({
        name: 'Winter Essentials',
        image: 'https://images.unsplash.com/photo-1515664069236-68a74c369d97?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Thermal Base Layer',
          store: 'Uniqlo',
          link: createProductLink('Thermal Base Layer', 'Uniqlo')
        }, {
          category: 'top',
          name: 'Wool Sweater',
          store: 'J.Crew',
          link: createProductLink('Wool Sweater', 'J.Crew')
        }, {
          category: 'top',
          name: 'Down Parka',
          store: 'Nordstrom',
          link: createProductLink('Down Parka', 'Nordstrom')
        }, {
          category: 'bottom',
          name: 'Insulated Pants',
          store: 'Uniqlo',
          link: createProductLink('Insulated Pants', 'Uniqlo')
        }, {
          category: 'accessories',
          name: 'Wool Beanie',
          store: 'H&M',
          link: createProductLink('Wool Beanie', 'H&M')
        }, {
          category: 'accessories',
          name: 'Insulated Gloves',
          store: 'The North Face',
          link: createProductLink('Insulated Gloves', 'The North Face')
        }, {
          category: 'accessories',
          name: 'Wool Scarf',
          store: 'Zara',
          link: createProductLink('Wool Scarf', 'Zara')
        }, {
          category: 'footwear',
          name: 'Insulated Winter Boots',
          store: 'Amazon Fashion',
          link: createProductLink('Insulated Winter Boots', 'Amazon Fashion')
        }]
      });
      outfits.push({
        name: 'Snow Day',
        image: 'https://images.unsplash.com/photo-1483197756298-6d0237917c68?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Heat-Tech Base Layer',
          store: 'Uniqlo',
          link: createProductLink('Heat-Tech Base Layer', 'Uniqlo')
        }, {
          category: 'top',
          name: 'Fleece Mid-Layer',
          store: 'The North Face',
          link: createProductLink('Fleece Mid-Layer', 'The North Face')
        }, {
          category: 'top',
          name: 'Waterproof Snow Jacket',
          store: 'Columbia',
          link: createProductLink('Waterproof Snow Jacket', 'Columbia')
        }, {
          category: 'bottom',
          name: 'Snow Pants',
          store: 'Columbia',
          link: createProductLink('Snow Pants', 'Columbia')
        }, {
          category: 'accessories',
          name: 'Thermal Face Mask',
          store: 'Amazon Fashion',
          link: createProductLink('Thermal Face Mask', 'Amazon Fashion')
        }, {
          category: 'accessories',
          name: 'Waterproof Gloves',
          store: 'The North Face',
          link: createProductLink('Waterproof Gloves', 'The North Face')
        }, {
          category: 'accessories',
          name: 'Hand Warmers',
          store: 'Amazon Fashion',
          link: createProductLink('Hand Warmers', 'Amazon Fashion')
        }, {
          category: 'footwear',
          name: 'Insulated Snow Boots',
          store: 'Columbia',
          link: createProductLink('Insulated Snow Boots', 'Columbia')
        }]
      });
      // Adding more cold weather options
      outfits.push({
        name: 'Winter Office',
        image: 'https://images.unsplash.com/photo-1521223890158-f9f7c3d5d504?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Cashmere Turtleneck',
          store: 'J.Crew',
          link: createProductLink('Cashmere Turtleneck', 'J.Crew')
        }, {
          category: 'top',
          name: 'Wool Blazer',
          store: 'Banana Republic',
          link: createProductLink('Wool Blazer', 'Banana Republic')
        }, {
          category: 'bottom',
          name: 'Wool Trousers',
          store: 'Banana Republic',
          link: createProductLink('Wool Trousers', 'Banana Republic')
        }, {
          category: 'accessories',
          name: 'Cashmere Scarf',
          store: 'Nordstrom',
          link: createProductLink('Cashmere Scarf', 'Nordstrom')
        }, {
          category: 'accessories',
          name: 'Leather Gloves',
          store: 'Nordstrom',
          link: createProductLink('Leather Gloves', 'Nordstrom')
        }, {
          category: 'footwear',
          name: 'Leather Boots with Wool Lining',
          store: 'ASOS',
          link: createProductLink('Leather Boots with Wool Lining', 'ASOS')
        }]
      });
      outfits.push({
        name: 'Winter Weekend',
        image: 'https://images.unsplash.com/photo-1511919884226-fd3cad34687c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Thermal Henley',
          store: 'Uniqlo',
          link: createProductLink('Thermal Henley', 'Uniqlo')
        }, {
          category: 'top',
          name: 'Cable Knit Sweater',
          store: 'H&M',
          link: createProductLink('Cable Knit Sweater', 'H&M')
        }, {
          category: 'top',
          name: 'Insulated Puffer Vest',
          store: 'Uniqlo',
          link: createProductLink('Insulated Puffer Vest', 'Uniqlo')
        }, {
          category: 'bottom',
          name: 'Fleece-Lined Jeans',
          store: "Levi's",
          link: createProductLink('Fleece-Lined Jeans', "Levi's")
        }, {
          category: 'accessories',
          name: 'Knit Pom Beanie',
          store: 'Urban Outfitters',
          link: createProductLink('Knit Pom Beanie', 'Urban Outfitters')
        }, {
          category: 'footwear',
          name: 'Shearling-Lined Boots',
          store: 'UGG',
          link: createProductLink('Shearling-Lined Boots', 'UGG')
        }]
      });
    }
    // Region-specific outfit examples
    // European cities
    if (['London', 'Paris', 'Berlin', 'Rome', 'Barcelona'].includes(cityName)) {
      outfits.push({
        name: 'European City Style',
        image: 'https://images.unsplash.com/photo-1475518112798-86ae358241eb?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Tailored Blazer',
          store: 'Zara',
          link: createProductLink('Tailored Blazer', 'Zara')
        }, {
          category: 'top',
          name: 'Fitted Button-Up Shirt',
          store: 'H&M',
          link: createProductLink('Fitted Button-Up Shirt', 'H&M')
        }, {
          category: 'bottom',
          name: 'Slim Fit Trousers',
          store: 'Mango',
          link: createProductLink('Slim Fit Trousers', 'Mango')
        }, {
          category: 'accessories',
          name: 'Leather Portfolio Bag',
          store: 'Zara',
          link: createProductLink('Leather Portfolio Bag', 'Zara')
        }, {
          category: 'accessories',
          name: 'Cashmere Scarf',
          store: 'Marks & Spencer',
          link: createProductLink('Cashmere Scarf', 'Marks & Spencer')
        }, {
          category: 'footwear',
          name: 'Leather Chelsea Boots',
          store: 'ASOS',
          link: createProductLink('Leather Chelsea Boots', 'ASOS')
        }]
      });
      // Adding more European styles
      outfits.push({
        name: 'Parisian Chic',
        image: 'https://images.unsplash.com/photo-1445633629932-0029acc44e88?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Breton Striped Shirt',
          store: 'Petit Bateau',
          link: createProductLink('Breton Striped Shirt', 'Petit Bateau')
        }, {
          category: 'bottom',
          name: 'High-Waisted Trousers',
          store: 'Mango',
          link: createProductLink('High-Waisted Trousers', 'Mango')
        }, {
          category: 'accessories',
          name: 'Silk Neck Scarf',
          store: 'Hermès',
          link: createProductLink('Silk Neck Scarf', 'Hermès')
        }, {
          category: 'footwear',
          name: 'Ballet Flats',
          store: 'Repetto',
          link: createProductLink('Ballet Flats', 'Repetto')
        }]
      });
    }
    // Asian cities
    if (['Tokyo', 'Seoul', 'Singapore', 'Hong Kong'].includes(cityName)) {
      outfits.push({
        name: 'Asian Street Style',
        image: 'https://images.unsplash.com/photo-1596703263926-eb0762ee17e4?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Oversized Graphic Tee',
          store: 'Uniqlo',
          link: createProductLink('Oversized Graphic Tee', 'Uniqlo')
        }, {
          category: 'top',
          name: 'Lightweight Bomber Jacket',
          store: 'GU',
          link: createProductLink('Lightweight Bomber Jacket', 'GU')
        }, {
          category: 'bottom',
          name: 'Wide-Leg Pants',
          store: 'Muji',
          link: createProductLink('Wide-Leg Pants', 'Muji')
        }, {
          category: 'accessories',
          name: 'Crossbody Bag',
          store: 'Muji',
          link: createProductLink('Crossbody Bag', 'Muji')
        }, {
          category: 'accessories',
          name: 'Fashionable Face Mask',
          store: 'Uniqlo',
          link: createProductLink('Fashionable Face Mask', 'Uniqlo')
        }, {
          category: 'footwear',
          name: 'Platform Sneakers',
          store: 'ASOS',
          link: createProductLink('Platform Sneakers', 'ASOS')
        }]
      });
      // Adding more Asian styles
      outfits.push({
        name: 'K-Fashion',
        image: 'https://images.unsplash.com/photo-1614548539924-5c1656e3b73b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Oversized Cardigan',
          store: 'Stylenanda',
          link: createProductLink('Oversized Cardigan', 'Stylenanda')
        }, {
          category: 'top',
          name: 'Cropped T-shirt',
          store: 'GU',
          link: createProductLink('Cropped T-shirt', 'GU')
        }, {
          category: 'bottom',
          name: 'High-Waist Wide Leg Pants',
          store: 'Åland',
          link: createProductLink('High-Waist Wide Leg Pants', 'Åland')
        }, {
          category: 'accessories',
          name: 'Minimalist Hair Clips',
          store: 'Stylenanda',
          link: createProductLink('Minimalist Hair Clips', 'Stylenanda')
        }, {
          category: 'footwear',
          name: 'Chunky White Sneakers',
          store: 'ASOS',
          link: createProductLink('Chunky White Sneakers', 'ASOS')
        }]
      });
    }
    // Style-specific outfits
    if (style) {
      switch (style.toLowerCase()) {
        case 'gothic':
          outfits.push({
            name: 'Gothic Style',
            image: 'https://images.unsplash.com/photo-1567784177951-6fa58317e16b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Black Band T-shirt',
              store: 'Hot Topic',
              link: createProductLink('Black Band T-shirt', 'Hot Topic')
            }, {
              category: 'top',
              name: 'Black Leather Jacket',
              store: 'ASOS',
              link: createProductLink('Black Leather Jacket', 'ASOS')
            }, {
              category: 'bottom',
              name: 'Ripped Black Skinny Jeans',
              store: 'Hot Topic',
              link: createProductLink('Ripped Black Skinny Jeans', 'Hot Topic')
            }, {
              category: 'accessories',
              name: 'Choker Necklace',
              store: 'Hot Topic',
              link: createProductLink('Choker Necklace', 'Hot Topic')
            }, {
              category: 'accessories',
              name: 'Studded Belt',
              store: 'Hot Topic',
              link: createProductLink('Studded Belt', 'Hot Topic')
            }, {
              category: 'accessories',
              name: 'Silver Ring Set',
              store: 'ASOS',
              link: createProductLink('Silver Ring Set', 'ASOS')
            }, {
              category: 'footwear',
              name: 'Platform Combat Boots',
              store: 'Hot Topic',
              link: createProductLink('Platform Combat Boots', 'Hot Topic')
            }]
          });
          outfits.push({
            name: 'Gothic Casual',
            image: 'https://images.unsplash.com/photo-1593351799227-75df2026356f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Black Graphic Hoodie',
              store: 'Hot Topic',
              link: createProductLink('Black Graphic Hoodie', 'Hot Topic')
            }, {
              category: 'bottom',
              name: 'Black Cargo Pants',
              store: 'ASOS',
              link: createProductLink('Black Cargo Pants', 'ASOS')
            }, {
              category: 'accessories',
              name: 'Chain Wallet',
              store: 'Hot Topic',
              link: createProductLink('Chain Wallet', 'Hot Topic')
            }, {
              category: 'footwear',
              name: 'Black Canvas High-Tops',
              store: 'Converse',
              link: createProductLink('Black Canvas High-Tops', 'Converse')
            }]
          });
          break;
        case 'bohemian':
          outfits.push({
            name: 'Boho Chic',
            image: 'https://images.unsplash.com/photo-1604339454148-1c23f0b5d156?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Embroidered Blouse',
              store: 'Free People',
              link: createProductLink('Embroidered Blouse', 'Free People')
            }, {
              category: 'bottom',
              name: 'Flowy Maxi Skirt',
              store: 'Anthropologie',
              link: createProductLink('Flowy Maxi Skirt', 'Anthropologie')
            }, {
              category: 'accessories',
              name: 'Layered Necklaces',
              store: 'Free People',
              link: createProductLink('Layered Necklaces', 'Free People')
            }, {
              category: 'accessories',
              name: 'Fringe Crossbody Bag',
              store: 'Anthropologie',
              link: createProductLink('Fringe Crossbody Bag', 'Anthropologie')
            }, {
              category: 'accessories',
              name: 'Printed Headscarf',
              store: 'Urban Outfitters',
              link: createProductLink('Printed Headscarf', 'Urban Outfitters')
            }, {
              category: 'footwear',
              name: 'Suede Ankle Boots',
              store: 'Free People',
              link: createProductLink('Suede Ankle Boots', 'Free People')
            }]
          });
          outfits.push({
            name: 'Boho Festival',
            image: 'https://images.unsplash.com/photo-1596944924616-7b38e7cfbfc8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Crochet Crop Top',
              store: 'Free People',
              link: createProductLink('Crochet Crop Top', 'Free People')
            }, {
              category: 'bottom',
              name: 'High-Waist Denim Shorts',
              store: 'Urban Outfitters',
              link: createProductLink('High-Waist Denim Shorts', 'Urban Outfitters')
            }, {
              category: 'accessories',
              name: 'Flower Crown',
              store: 'Etsy',
              link: createProductLink('Flower Crown', 'Etsy')
            }, {
              category: 'accessories',
              name: 'Fringe Vest',
              store: 'Free People',
              link: createProductLink('Fringe Vest', 'Free People')
            }, {
              category: 'footwear',
              name: 'Gladiator Sandals',
              store: 'Anthropologie',
              link: createProductLink('Gladiator Sandals', 'Anthropologie')
            }]
          });
          break;
        case 'sporty':
          outfits.push({
            name: 'Athleisure',
            image: 'https://images.unsplash.com/photo-1483721310020-03333e577078?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Performance T-shirt',
              store: 'Nike',
              link: createProductLink('Performance T-shirt', 'Nike')
            }, {
              category: 'top',
              name: 'Lightweight Track Jacket',
              store: 'Adidas',
              link: createProductLink('Lightweight Track Jacket', 'Adidas')
            }, {
              category: 'bottom',
              name: 'Athletic Leggings',
              store: 'Lululemon',
              link: createProductLink('Athletic Leggings', 'Lululemon')
            }, {
              category: 'accessories',
              name: 'Sports Cap',
              store: 'Nike',
              link: createProductLink('Sports Cap', 'Nike')
            }, {
              category: 'accessories',
              name: 'Fitness Tracker Watch',
              store: 'Amazon Fashion',
              link: createProductLink('Fitness Tracker Watch', 'Amazon Fashion')
            }, {
              category: 'footwear',
              name: 'Running Shoes',
              store: 'Nike',
              link: createProductLink('Running Shoes', 'Nike')
            }]
          });
          outfits.push({
            name: 'Sport Casual',
            image: 'https://images.unsplash.com/photo-1517940310602-26535839fe84?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Oversized Hoodie',
              store: 'Adidas',
              link: createProductLink('Oversized Hoodie', 'Adidas')
            }, {
              category: 'bottom',
              name: 'Track Pants',
              store: 'Nike',
              link: createProductLink('Track Pants', 'Nike')
            }, {
              category: 'accessories',
              name: 'Crossbody Fanny Pack',
              store: 'Adidas',
              link: createProductLink('Crossbody Fanny Pack', 'Adidas')
            }, {
              category: 'footwear',
              name: 'Classic Sneakers',
              store: 'Nike',
              link: createProductLink('Classic Sneakers', 'Nike')
            }]
          });
          break;
        case 'formal':
          outfits.push({
            name: 'Business Professional',
            image: 'https://images.unsplash.com/photo-1580657018950-c7f7d6a6d990?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Tailored Suit Jacket',
              store: 'Nordstrom',
              link: createProductLink('Tailored Suit Jacket', 'Nordstrom')
            }, {
              category: 'top',
              name: 'Crisp Dress Shirt',
              store: 'Brooks Brothers',
              link: createProductLink('Crisp Dress Shirt', 'Brooks Brothers')
            }, {
              category: 'bottom',
              name: 'Matching Suit Pants',
              store: 'Nordstrom',
              link: createProductLink('Matching Suit Pants', 'Nordstrom')
            }, {
              category: 'accessories',
              name: 'Silk Tie',
              store: 'Brooks Brothers',
              link: createProductLink('Silk Tie', 'Brooks Brothers')
            }, {
              category: 'accessories',
              name: 'Leather Belt',
              store: 'Nordstrom',
              link: createProductLink('Leather Belt', 'Nordstrom')
            }, {
              category: 'accessories',
              name: 'Classic Watch',
              store: 'Nordstrom',
              link: createProductLink('Classic Watch', 'Nordstrom')
            }, {
              category: 'footwear',
              name: 'Oxford Dress Shoes',
              store: 'Nordstrom',
              link: createProductLink('Oxford Dress Shoes', 'Nordstrom')
            }]
          });
          outfits.push({
            name: 'Business Casual',
            image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Button-Down Shirt',
              store: 'J.Crew',
              link: createProductLink('Button-Down Shirt', 'J.Crew')
            }, {
              category: 'top',
              name: 'Navy Blazer',
              store: 'Banana Republic',
              link: createProductLink('Navy Blazer', 'Banana Republic')
            }, {
              category: 'bottom',
              name: 'Khaki Chinos',
              store: 'Banana Republic',
              link: createProductLink('Khaki Chinos', 'Banana Republic')
            }, {
              category: 'accessories',
              name: 'Leather Laptop Bag',
              store: 'Fossil',
              link: createProductLink('Leather Laptop Bag', 'Fossil')
            }, {
              category: 'footwear',
              name: 'Leather Loafers',
              store: 'ASOS',
              link: createProductLink('Leather Loafers', 'ASOS')
            }]
          });
          break;
        case 'minimalist':
          outfits.push({
            name: 'Clean Minimalist',
            image: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Solid Color Crewneck',
              store: 'Uniqlo',
              link: createProductLink('Solid Color Crewneck', 'Uniqlo')
            }, {
              category: 'bottom',
              name: 'Straight-Leg Trousers',
              store: 'COS',
              link: createProductLink('Straight-Leg Trousers', 'COS')
            }, {
              category: 'accessories',
              name: 'Leather Tote Bag',
              store: 'Everlane',
              link: createProductLink('Leather Tote Bag', 'Everlane')
            }, {
              category: 'accessories',
              name: 'Minimal Gold Necklace',
              store: 'COS',
              link: createProductLink('Minimal Gold Necklace', 'COS')
            }, {
              category: 'footwear',
              name: 'White Leather Sneakers',
              store: 'Everlane',
              link: createProductLink('White Leather Sneakers', 'Everlane')
            }]
          });
          outfits.push({
            name: 'Scandinavian Minimal',
            image: 'https://images.unsplash.com/photo-1501699169021-3759ee435d66?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
            items: [{
              category: 'top',
              name: 'Oversized White Shirt',
              store: 'COS',
              link: createProductLink('Oversized White Shirt', 'COS')
            }, {
              category: 'bottom',
              name: 'Black Tailored Trousers',
              store: 'Arket',
              link: createProductLink('Black Tailored Trousers', 'Arket')
            }, {
              category: 'accessories',
              name: 'Minimal Watch',
              store: 'Nordgreen',
              link: createProductLink('Minimal Watch', 'Nordgreen')
            }, {
              category: 'footwear',
              name: 'Leather Slip-ons',
              store: 'Everlane',
              link: createProductLink('Leather Slip-ons', 'Everlane')
            }]
          });
          break;
      }
    }
    // Make sure we have at least 2 outfits
    if (outfits.length < 2) {
      outfits.push({
        name: 'Versatile Casual',
        image: 'https://images.unsplash.com/photo-1516762689617-e1cffcef479d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        items: [{
          category: 'top',
          name: 'Classic T-shirt',
          store: 'Uniqlo',
          link: createProductLink('Classic T-shirt', 'Uniqlo')
        }, {
          category: 'bottom',
          name: 'Straight-Leg Jeans',
          store: "Levi's",
          link: createProductLink('Straight-Leg Jeans', "Levi's")
        }, {
          category: 'accessories',
          name: 'Leather Watch',
          store: 'Nordstrom',
          link: createProductLink('Leather Watch', 'Nordstrom')
        }, {
          category: 'footwear',
          name: 'Canvas Sneakers',
          store: 'ASOS',
          link: createProductLink('Canvas Sneakers', 'ASOS')
        }]
      });
    }
    // Return the generated outfits
    return outfits;
  };
  // Category-specific image examples
  const getCategoryImages = category => {
    // Ensure all images are valid and accessible
    const verifiedImages = {
      top: [{
        name: 'Casual T-shirt',
        image: 'https://images.unsplash.com/photo-1576871337622-98d48d1cf531?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'casual'
      }, {
        name: 'Formal Shirt',
        image: 'https://images.unsplash.com/photo-1598033129183-c4f50c736f10?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'formal'
      }, {
        name: 'Sweater',
        image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'casual'
      }],
      bottom: [{
        name: 'Jeans',
        image: 'https://images.unsplash.com/photo-1604176354204-9268737828e4?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'casual'
      }, {
        name: 'Dress Pants',
        image: 'https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'formal'
      }, {
        name: 'Skirt',
        image: 'https://images.unsplash.com/photo-1583496661160-fb5886a0aaaa?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'casual'
      }],
      accessories: [{
        name: 'Sunglasses',
        image: 'https://images.unsplash.com/photo-1577803645773-f96470509666?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'casual'
      }, {
        name: 'Watch',
        image: 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'formal'
      }, {
        name: 'Scarf',
        image: 'https://images.unsplash.com/photo-1520903920243-00d872a2d1c9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'winter'
      }],
      footwear: [{
        name: 'Sneakers',
        image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'casual'
      }, {
        name: 'Dress Shoes',
        image: 'https://images.unsplash.com/photo-1478146896981-b80fe463b330?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'formal'
      }, {
        name: 'Boots',
        image: 'https://images.unsplash.com/photo-1608256246200-53e635b5b65f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        style: 'winter'
      }]
    };
    // Filter based on user style if available
    if (userProfile.style && userProfile.style.toLowerCase() !== '') {
      const userStyle = userProfile.style.toLowerCase();
      const filteredImages = verifiedImages[category].filter(item => item.style === userStyle || item.style === 'casual');
      // If we have style-specific images, return them, otherwise fall back to all images
      if (filteredImages.length > 0) {
        return filteredImages;
      }
    }
    // If no style match or no style specified, return all images for the category
    return verifiedImages[category];
  };
  const recommendations = getRecommendations();
  const recommendedStores = getStoreRecommendations();
  const allOutfitExamples = getOutfitExamples();
  // Initialize displayed outfits if empty
  useEffect(() => {
    if (displayedOutfits.length === 0) {
      setDisplayedOutfits(allOutfitExamples.slice(0, 8));
    }
  }, [allOutfitExamples]);
  // Handle thumbs up for outfit
  const handleOutfitThumbsUp = outfitId => {
    setFeedback(prev => ({
      ...prev,
      [outfitId]: 'up'
    }));
  };
  // Handle thumbs down for outfit - replace the outfit
  const handleOutfitThumbsDown = outfitId => {
    // Mark as disliked in feedback
    setFeedback(prev => ({
      ...prev,
      [outfitId]: 'down'
    }));
    // Find unused outfits to use as replacements
    const usedOutfitIds = displayedOutfits.map(outfit => outfit.name);
    const availableReplacements = allOutfitExamples.filter(outfit => !usedOutfitIds.includes(outfit.name) && !feedback[outfit.name] === 'down');
    if (availableReplacements.length === 0) {
      // If no replacements are available, just keep the current outfit
      return;
    }
    // Choose a random replacement
    const randomIndex = Math.floor(Math.random() * availableReplacements.length);
    const replacementOutfit = availableReplacements[randomIndex];
    // Update the displayed outfits
    setDisplayedOutfits(prev => {
      return prev.map(outfit => {
        if (outfit.name === outfitId) {
          return replacementOutfit;
        }
        return outfit;
      });
    });
  };
  const outfitImages = {
    warm: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    moderate: 'https://images.unsplash.com/photo-1554412933-514a83d2f3c8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    cool: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
  };
  // Get different outfit images based on user style preference
  const getOutfitImage = () => {
    const {
      temperature
    } = weatherData;
    const {
      style
    } = userProfile;
    // Base image on temperature
    let baseImage;
    if (temperature > 75) baseImage = outfitImages.warm;else if (temperature > 60) baseImage = outfitImages.moderate;else baseImage = outfitImages.cool;
    // Override with style-specific images if available
    if (style) {
      const styleImages = {
        gothic: 'https://images.unsplash.com/photo-1581044777550-4cfa60707c03?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        classic: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        bohemian: 'https://images.unsplash.com/photo-1518622358385-8ea7d0794bf6?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        formal: 'https://images.unsplash.com/photo-1507680434567-5739c80be1ac?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        sporty: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        minimalist: 'https://images.unsplash.com/photo-1513094735237-8f2714d57c13?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        vintage: 'https://images.unsplash.com/photo-1520423465871-0866049020b7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        streetwear: 'https://images.unsplash.com/photo-1523398002811-999ca8dec234?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
      };
      return styleImages[style.toLowerCase()] || baseImage;
    }
    return baseImage;
  };
  // Display temperature range description
  const getTemperatureRange = temp => {
    if (temp >= 85) return 'Very Hot';
    if (temp >= 70) return 'Warm';
    if (temp >= 55) return 'Moderate';
    if (temp >= 40) return 'Cool';
    return 'Cold';
  };
  // Function to open the outfit modal
  const openOutfitModal = outfit => {
    setSelectedOutfit(outfit);
    setShowOutfitModal(true);
  };
  // Function to handle clicking on a category-specific image
  const handleCategoryImageClick = categoryImage => {
    // Create a mock outfit object for the modal
    const mockOutfit = {
      name: categoryImage.name,
      image: categoryImage.image,
      items: [{
        category: categoryImage.category || 'top',
        name: categoryImage.name,
        store: 'Recommended Store',
        link: '#'
      }]
    };
    setSelectedOutfit(mockOutfit);
    setShowOutfitModal(true);
  };
  return <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 h-full" data-id="element-63">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-64">
        Today's Outfit Recommendation
      </h2>
      <div className="mb-3" data-id="element-65">
        <span className="text-xs sm:text-sm font-medium text-gray-600" data-id="element-66">
          {weatherData.cityName || ''} •{' '}
          {getTemperatureRange(weatherData.temperature)} (
          {weatherData.temperature}°F)
        </span>
        {userProfile.style && <span className="ml-2 text-xs sm:text-sm font-medium text-indigo-600" data-id="element-67">
            • {userProfile.style} style
            {userProfile.bodyType && ` • ${userProfile.bodyType} body type`}
          </span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-68">
        <div className="col-span-1" data-id="element-69">
          <div className="aspect-w-3 aspect-h-4 rounded-lg overflow-hidden" data-id="element-70">
            <img src={getOutfitImage()} alt="Recommended outfit" className="object-cover w-full h-full" data-id="element-71" />
          </div>
        </div>
        <div className="col-span-1 md:col-span-2" data-id="element-72">
          <div className="space-y-4 sm:space-y-6" data-id="element-73">
            <div data-id="element-74">
              <h3 className="flex items-center text-lg font-medium text-gray-800" data-id="element-75">
                <ShirtIcon className="mr-2 h-5 w-5 text-indigo-600" data-id="element-76" />
                Top
              </h3>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-77">
                {recommendations.top.map((item, index) => <span key={index} className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm" data-id="element-78">
                    {item}
                  </span>)}
              </div>
              {/* Top Examples - with category-specific images */}
              <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-79">
                {getCategoryImages('top').map((item, idx) => <div key={idx} className="relative rounded-md overflow-hidden cursor-pointer" onClick={() => handleCategoryImageClick({
                ...item,
                category: 'top'
              })} data-id="element-80">
                    <img src={item.image} alt={item.name} className="h-24 w-full object-cover" data-id="element-81" />
                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" data-id="element-82">
                      <EyeIcon className="h-6 w-6 text-white" data-id="element-83" />
                    </div>
                  </div>)}
              </div>
            </div>
            <div data-id="element-84">
              <h3 className="flex items-center text-lg font-medium text-gray-800" data-id="element-85">
                <div className="mr-2 h-5 w-5 text-indigo-600" data-id="element-86" />
                Bottom
              </h3>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-87">
                {recommendations.bottom.map((item, index) => <span key={index} className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm" data-id="element-88">
                    {item}
                  </span>)}
              </div>
              {/* Bottom Examples - with category-specific images */}
              <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-89">
                {getCategoryImages('bottom').map((item, idx) => <div key={idx} className="relative rounded-md overflow-hidden cursor-pointer" onClick={() => handleCategoryImageClick({
                ...item,
                category: 'bottom'
              })} data-id="element-90">
                    <img src={item.image} alt={item.name} className="h-24 w-full object-cover" data-id="element-91" />
                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" data-id="element-92">
                      <EyeIcon className="h-6 w-6 text-white" data-id="element-93" />
                    </div>
                  </div>)}
              </div>
            </div>
            <div data-id="element-94">
              <h3 className="flex items-center text-lg font-medium text-gray-800" data-id="element-95">
                {weatherData.condition.toLowerCase().includes('rain') ? <UmbrellaIcon className="mr-2 h-5 w-5 text-indigo-600" data-id="element-96" /> : <SunIcon className="mr-2 h-5 w-5 text-indigo-600" data-id="element-97" />}
                Accessories
              </h3>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-98">
                {recommendations.accessories.map((item, index) => <span key={index} className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm" data-id="element-99">
                    {item}
                  </span>)}
              </div>
              {/* Accessories Examples - with category-specific images */}
              <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-100">
                {getCategoryImages('accessories').map((item, idx) => <div key={idx} className="relative rounded-md overflow-hidden cursor-pointer" onClick={() => handleCategoryImageClick({
                ...item,
                category: 'accessories'
              })} data-id="element-101">
                    <img src={item.image} alt={item.name} className="h-24 w-full object-cover" data-id="element-102" />
                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" data-id="element-103">
                      <EyeIcon className="h-6 w-6 text-white" data-id="element-104" />
                    </div>
                  </div>)}
              </div>
            </div>
            <div data-id="element-105">
              <h3 className="flex items-center text-lg font-medium text-gray-800" data-id="element-106">
                <div className="mr-2 h-5 w-5 text-indigo-600" data-id="element-107" />
                Footwear
              </h3>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-108">
                {recommendations.footwear.map((item, index) => <span key={index} className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm" data-id="element-109">
                    {item}
                  </span>)}
              </div>
              {/* Footwear Examples - with category-specific images */}
              <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-110">
                {getCategoryImages('footwear').map((item, idx) => <div key={idx} className="relative rounded-md overflow-hidden cursor-pointer" onClick={() => handleCategoryImageClick({
                ...item,
                category: 'footwear'
              })} data-id="element-111">
                    <img src={item.image} alt={item.name} className="h-24 w-full object-cover" data-id="element-112" />
                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" data-id="element-113">
                      <EyeIcon className="h-6 w-6 text-white" data-id="element-114" />
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Store Recommendations */}
            <div className="pt-2 border-t border-gray-100" data-id="element-115">
              <h3 className="flex items-center text-lg font-medium text-gray-800 mb-3" data-id="element-116">
                <ShoppingBagIcon className="mr-2 h-5 w-5 text-indigo-600" data-id="element-117" />
                Where to Shop
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-118">
                {recommendedStores.map((store, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm" data-id="element-119">
                    {store}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Complete Outfit Examples */}
      <div className="mt-6 sm:mt-8" data-id="element-120">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 sm:mb-4" data-id="element-121">
          Complete Outfit Examples
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-122">
          {displayedOutfits.map((outfit, index) => <div key={index} className="bg-gray-50 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => openOutfitModal(outfit)} data-id="element-123">
              <div className="aspect-w-1 aspect-h-1 relative" data-id="element-124">
                <img src={outfit.image} alt={outfit.name} className="object-cover w-full h-full" data-id="element-125" />
                <div className="absolute top-2 right-2 flex space-x-1" data-id="element-126">
                  <button onClick={e => {
                e.stopPropagation();
                handleOutfitThumbsUp(outfit.name);
              }} className={`p-1 rounded-full ${feedback[outfit.name] === 'up' ? 'bg-green-500 text-white' : 'bg-white/80 text-gray-600 hover:bg-green-100'}`} data-id="element-127">
                    <ThumbsUpIcon className="h-4 w-4" data-id="element-128" />
                  </button>
                  <button onClick={e => {
                e.stopPropagation();
                handleOutfitThumbsDown(outfit.name);
              }} className={`p-1 rounded-full ${feedback[outfit.name] === 'down' ? 'bg-red-500 text-white' : 'bg-white/80 text-gray-600 hover:bg-red-100'}`} data-id="element-129">
                    <ThumbsDownIcon className="h-4 w-4" data-id="element-130" />
                  </button>
                </div>
              </div>
              <div className="p-4" data-id="element-131">
                <div className="flex justify-between items-center" data-id="element-132">
                  <h4 className="font-medium text-gray-900" data-id="element-133">{outfit.name}</h4>
                  <button className="flex items-center text-indigo-600 hover:text-indigo-800 text-sm" onClick={e => {
                e.stopPropagation();
                openOutfitModal(outfit);
              }} data-id="element-134">
                    <EyeIcon className="h-4 w-4 mr-1" data-id="element-135" />
                    View
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-136">
                  {outfit.items.length} items
                </p>
              </div>
            </div>)}
        </div>
      </div>
      {/* Outfit Modal */}
      {showOutfitModal && selectedOutfit && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-137">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" data-id="element-138">
            <div className="flex justify-between items-center p-4 border-b" data-id="element-139">
              <h3 className="text-xl font-semibold" data-id="element-140">
                {selectedOutfit.name} Outfit
              </h3>
              <button onClick={() => setShowOutfitModal(false)} className="text-gray-500 hover:text-gray-700" data-id="element-141">
                <XIcon className="h-6 w-6" data-id="element-142" />
              </button>
            </div>
            <div className="p-6" data-id="element-143">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-144">
                <div data-id="element-145">
                  <div className="aspect-w-3 aspect-h-4 rounded-lg overflow-hidden" data-id="element-146">
                    <img src={selectedOutfit.image} alt={selectedOutfit.name} className="object-cover w-full h-full" data-id="element-147" />
                  </div>
                </div>
                <div data-id="element-148">
                  <h4 className="font-medium text-gray-900 mb-4" data-id="element-149">
                    Items in this outfit:
                  </h4>
                  <div className="space-y-4" data-id="element-150">
                    {['top', 'bottom', 'accessories', 'footwear'].map(category => {
                  const items = selectedOutfit.items.filter(item => item.category === category);
                  if (items.length === 0) return null;
                  return <div key={category} data-id="element-151">
                            <h5 className="font-medium text-gray-800 capitalize mb-2" data-id="element-152">
                              {category}
                            </h5>
                            <ul className="space-y-2" data-id="element-153">
                              {items.map((item, idx) => <li key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded" data-id="element-154">
                                  <span className="text-gray-800" data-id="element-155">
                                    {item.name}
                                  </span>
                                  <div className="flex items-center" data-id="element-156">
                                    <span className="text-sm text-gray-500 mr-2" data-id="element-157">
                                      {item.store}
                                    </span>
                                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="flex items-center text-indigo-600 hover:text-indigo-800 text-sm" onClick={e => e.stopPropagation()} data-id="element-158">
                                      <LinkIcon className="h-3 w-3 mr-1" data-id="element-159" />
                                      Shop
                                    </a>
                                  </div>
                                </li>)}
                            </ul>
                          </div>;
                })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}